// Get Receipt — the driver's own way to ask for a receipt after charging.
//
// This sheet rendered, accepted an address, and its submit handler ran
// `console.log("Email submitted:", email)`. Nothing was persisted and nothing
// was sent: a driver who typed an address here got no receipt, and no error
// either. It is the last chance for anyone the backend could not route a
// receipt to on its own — a guest whose plate matches no registered vehicle,
// or a no-charge session that skipped the payment step where the address is
// normally collected.
//
// It reuses patchReceiptInferred, the same endpoint the registered-vehicle
// flow uses, rather than a second receipt path. The backend stores the address
// and, when the session is finalized and still owed a receipt, enqueues exactly
// one — keyed on the session, so a double tap or a retry cannot produce two.
import React, { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import FormInput from '../../../Forms/FormInput';
import { useSession } from '../../../../context/SessionContext';
import { patchReceiptInferred } from '../../../../api/sessionsApi';

// Deliberately permissive: the backend validates authoritatively, and this only
// exists to catch an obvious typo before a round trip.
const looksLikeEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

// What the backend did with the address. Only 'queued' and 'not_finalized'
// mean a receipt will go to the address just typed; the others mean a receipt
// was already owed or sent, and naming the new address would be untrue.
const OUTCOME_COPY = {
    already_queued: 'Your receipt is already on its way.',
    already_sent: 'Your receipt has already been sent.',
};

const CaptureEmail = ({ show, onClose, onOpenChange, onResult }) => {
    const { state } = useSession();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error
    const [error, setError] = useState(null);
    const [outcome, setOutcome] = useState(null);

    // Lets the host pause anything that would pull the screen away mid-entry,
    // such as the summary's auto-exit countdown.
    useEffect(() => { onOpenChange?.(Boolean(show)); }, [show, onOpenChange]);

    const sessionUid = state?.sessionUid || null;
    const sending = status === 'sending';
    const canSubmit = looksLikeEmail(email) && !sending;

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        // Clear a previous failure as soon as the driver edits, so the message
        // describes what they have now rather than what they had.
        if (status === 'error') { setStatus('idle'); setError(null); }
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;

        if (!sessionUid) {
            setStatus('error');
            setError("We couldn't find your session. Please reopen your receipt and try again.");
            return;
        }

        setStatus('sending');
        setError(null);
        try {
            const result = await patchReceiptInferred(sessionUid, {
                email: email.trim(),
                fullName: state?.receiptInfo?.fullName || null,
                // Distinguishes an address the driver typed here from one
                // inferred from the vehicle registry, so receipt_contact_source
                // stays honest about where it came from.
                source: 'guest_entered',
            });

            if (result?.ok === false) {
                setStatus('error');
                setError(result.message || "We couldn't save that address. Please try again.");
                return;
            }
            setOutcome(result?.email?.reason || null);
            setStatus('sent');
            onResult?.(result);
        } catch (err) {
            setStatus('error');
            setError(err?.message || "We couldn't reach GreenCharge. Please try again.");
        }
    };

    const handleClose = () => {
        onClose?.();
        // Reset only after closing, so the success line stays visible while the
        // sheet is dismissing.
        setTimeout(() => { setStatus('idle'); setError(null); setEmail(''); setOutcome(null); }, 250);
    };

    return (
        <Offcanvas
            show={show}
            placement="bottom"
            onHide={handleClose}
            style={{ height: 'auto', maxHeight: '90vh' }}
            aria-labelledby="capture-email-title"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title id="capture-email-title">Get Receipt</Offcanvas.Title>
            </Offcanvas.Header>
            <hr />
            <Offcanvas.Body style={{ overflowY: 'auto' }}>
                {status === 'sent' ? (
                    <div role="status" data-testid="capture-email-success">
                        {OUTCOME_COPY[outcome] ? (
                            <p className="mb-1"><strong>{OUTCOME_COPY[outcome]}</strong></p>
                        ) : (
                            <>
                                <p className="mb-1"><strong>Receipt on its way</strong></p>
                                <p className="text-muted">
                                    We’ll email your receipt to {email.trim()}.
                                </p>
                            </>
                        )}
                        <button
                            type="button"
                            className="btn btn-dark mt-2 w-100 p-3"
                            onClick={handleClose}
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        <p>Please enter your email address below to receive your receipt.</p>
                        <form
                            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                            noValidate
                        >
                            <FormInput
                                type="email"
                                name="email"
                                id="capture-receipt-email"
                                placeholder="Enter your email"
                                value={email}
                                onValueChange={handleEmailChange}
                                enterKeyHint="done"
                                disabled={sending}
                            />

                            {status === 'error' && (
                                <p
                                    className="text-danger mt-2 mb-0"
                                    role="alert"
                                    data-testid="capture-email-error"
                                >
                                    {error}
                                </p>
                            )}

                            <button
                                type="submit"
                                className="btn btn-dark mt-3 w-100 p-3"
                                data-testid="capture-email-submit"
                                disabled={!canSubmit}
                            >
                                {sending ? 'Sending…' : 'Submit'}
                            </button>
                        </form>
                    </>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default CaptureEmail;
